import { MAZE_MOVE_INPUT_COOLDOWN_MS } from '@/constants/maze';
import { cellCenter } from '@/lib/maze/maze-canvas-cell-helpers';
import {
  areOppositeDirections,
  getDirectionVector,
  getOppositeDirection,
} from '@/lib/maze/maze-gen';
import { useDemoStore } from '@/store/demo';
import { Direction, type MazeCell } from '@/types/maze';
import { dispatchMazePickSideAudio } from '@/types/maze-audio-events';
import { isDemoTutorialInputBlocked } from '@/utils/maze/demo-tutorial';
import { gsap } from 'gsap';
import type * as PIXI from 'pixi.js';
import type { RefObject } from 'react';
import type { ActiveTravelSegment, PlayerState, TravelArrivalOptions } from './maze-canvas-types';
import { PlayerState as PlayerStateEnum } from './maze-canvas-types';

export interface StartMovementContext {
  readonly lastMoveInputAtRef: RefObject<number>;
  readonly playerStateRef: RefObject<PlayerState>;
  readonly activeTravelRef: RefObject<ActiveTravelSegment | null>;
  readonly playerRef: RefObject<PIXI.Container | null>;

  readonly applyTravelArrivalRef: RefObject<
    | ((
        nextX: number,
        nextY: number,
        direction: Direction,
        recordLiveMoveOnArrival: boolean,
        options: TravelArrivalOptions,
      ) => void)
    | null
  >;
  readonly goalCellRef: RefObject<{ x: number; y: number }>;
  readonly positionRef: RefObject<{ x: number; y: number }>;
  readonly mazeRef: RefObject<MazeCell[][]>;
  readonly junctionArrowRevealTokenRef: RefObject<number>;
  readonly moveToCellRef: RefObject<(startX: number, startY: number, dir: Direction) => void>;
  readonly cancelIdleFlush: () => void;
  readonly clearJunctionArrows: () => void;
}

export function createStartMovement(ctx: StartMovementContext): (direction: Direction) => void {
  return (direction: Direction) => {
    const { demoTutorialStep } = useDemoStore.getState();
    if (isDemoTutorialInputBlocked(demoTutorialStep)) {
      return;
    }

    const now = performance.now();

    // Mid-roll redirect: same cooldown as idle/junction so inhuman tap rates cannot
    // chain redirects faster than MAZE_MOVE_INPUT_COOLDOWN_MS.
    if (ctx.playerStateRef.current === PlayerStateEnum.TRAVELING) {
      const seg = ctx.activeTravelRef.current;
      const player = ctx.playerRef.current;
      if (!seg || !player) {
        return;
      }

      if (direction === seg.direction) {
        return;
      }

      if (now - ctx.lastMoveInputAtRef.current < MAZE_MOVE_INPUT_COOLDOWN_MS) {
        return;
      }

      gsap.killTweensOf(player);


      const { dx, dy } = getDirectionVector(seg.direction);
      const originX = seg.nextX - dx;
      const originY = seg.nextY - dy;
      const isOppositeRedirect = areOppositeDirections(seg.direction, direction);

      if (isOppositeRedirect) {
        const { x: originCenterX, y: originCenterY } = cellCenter(originX, originY);
        player.x = originCenterX;
        player.y = originCenterY;
        ctx.activeTravelRef.current = null;

        const applyArrival = ctx.applyTravelArrivalRef.current;
        if (!applyArrival) {
          return;
        }

        ctx.cancelIdleFlush();
        applyArrival(originX, originY, getOppositeDirection(seg.direction), false, {
          skipAutoNext: true,
        });

        ctx.lastMoveInputAtRef.current = now;
        ctx.junctionArrowRevealTokenRef.current += 1;
        ctx.clearJunctionArrows();
        return;
      }

      player.x = seg.targetX;
      player.y = seg.targetY;
      ctx.activeTravelRef.current = null;

      const applyArrival = ctx.applyTravelArrivalRef.current;
      if (!applyArrival) {
        return;
      }
      applyArrival(seg.nextX, seg.nextY, seg.direction, seg.recordLiveMoveOnArrival, {
        skipAutoNext: true,
      });

      const g = ctx.goalCellRef.current;
      const pos = ctx.positionRef.current;
      if (pos.x === g.x && pos.y === g.y) {
        return;
      }

      const currentCell = ctx.mazeRef.current[pos.y]?.[pos.x];
      if (!currentCell || !(currentCell.walls & direction)) {
        return;
      }

      ctx.lastMoveInputAtRef.current = now;
      ctx.junctionArrowRevealTokenRef.current += 1;

      if (direction !== seg.direction) {
        dispatchMazePickSideAudio();
      }
      ctx.clearJunctionArrows();
      ctx.playerStateRef.current = PlayerStateEnum.TRAVELING;
      ctx.moveToCellRef.current(pos.x, pos.y, direction);
      return;
    }

    if (now - ctx.lastMoveInputAtRef.current < MAZE_MOVE_INPUT_COOLDOWN_MS) {
      return;
    }

    ctx.cancelIdleFlush();

    const { x, y } = ctx.positionRef.current;
    const currentCell = ctx.mazeRef.current[y]?.[x];
    if (!currentCell || !(currentCell.walls & direction)) {
      return;
    }

    ctx.lastMoveInputAtRef.current = now;

    ctx.junctionArrowRevealTokenRef.current += 1;

    if (ctx.playerStateRef.current === PlayerStateEnum.JUNCTION_WAIT) {
      dispatchMazePickSideAudio();
    }
    ctx.clearJunctionArrows();
    ctx.playerStateRef.current = PlayerStateEnum.TRAVELING;
    ctx.moveToCellRef.current(x, y, direction);
  };
}
